import { useFlow } from './StageWrapper';
import { Dialog } from './FarChat/ChatFeedbackOverlays';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  requestMarketResearchNotification,
  type MarketResearchNotificationContext,
  type MarketResearchNotificationRequestResult,
} from '../api/workflowNotifications';
import { useToast } from './Toast/ToastProvider';

export interface MarketResearchNotifyControlsProps {
  flowId: string;
  context: MarketResearchNotificationContext;
  /** Only the owner may start the existing sharing dialog from this recovery path. */
  isOwner?: boolean;
  onOpenShare?: (context: MarketResearchNotificationContext) => void;
  /** Chat binds this mutation to its authenticated controller; no flow/source crosses the browser boundary. */
  onRequestNotification?: (requestId: string) => Promise<MarketResearchNotificationRequestResult>;
  /** Open the standard confirmation once a prerequisite stage save has committed. */
  autoOpen?: boolean;
  onAutoOpen?: () => void;
}

const copyForContext = (context: MarketResearchNotificationContext) => context === 'questions'
  ? {
      button: 'Notify mission partners to answer questions',
      confirmation: 'This will notify mission partners by creating a private notification asking them to answer questions in this workflow.',
      noRecipients: 'This workflow is not shared with any mission partners.',
    }
  : context === 'brand_name_ja'
    ? {
      button: 'Handoff to CO/CS',
      confirmation: 'This will hand off this Brand-name J&A to eligible CO/CS users for review.',
      noRecipients: 'This workflow is not shared with any CO/CS.',
    }
    : context === 'section_c_handoff'
      ? {
        button: 'Handoff to CO/CS',
        confirmation: 'This will notify eligible CO/CS users that you completed Market Research through Section C and request contracting review.',
        noRecipients: 'This workflow is not shared with any CO/CS.',
      }
    : {
      button: 'Notify CO/CS for contracting review',
      confirmation: 'This will create a private Waypoint notification asking the CO/CS to review the completed Product Discovery work.',
      noRecipients: 'This workflow is not shared with any CO/CS.',
    };

const browserRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `request-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const createdNotificationCopy = (context: MarketResearchNotificationContext, notifiedUsers?: string[]) => {
  if (context === 'product_discovery_review' || context === 'section_c_handoff') return 'Notification created for contracting review.';
  const users = Array.from(new Set((notifiedUsers || []).filter((user) => typeof user === 'string' && user.length > 0)));
  if (users.length === 0) return 'Notification created.';
  if (users.length === 1) return `Notification created for ${users[0]}.`;
  if (users.length === 2) return `Notification created for ${users[0]} and ${users[1]}.`;
  return `Notification created for ${users.slice(0, -1).join(', ')}, and ${users[users.length - 1]}.`;
};

/**
 * Fixed-context Market Research notifications. Recipient selection remains
 * entirely server-owned; this component has no roster, address, or count data.
 */
const MarketResearchNotifyControls: React.FC<MarketResearchNotifyControlsProps> = ({
  flowId,
  context,
  isOwner = false,
  onOpenShare,
  onRequestNotification,
  autoOpen = false,
  onAutoOpen,
}) => {
  const { isViewOnly } = useFlow();
  const accessRef = useRef({ flowId, context, isViewOnly });
  accessRef.current = { flowId, context, isViewOnly };
  const mayNotify = () => !accessRef.current.isViewOnly && accessRef.current.flowId === flowId && accessRef.current.context === context;
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Defer opening the parent share dialog until this confirmation dialog has
  // completely exited. This avoids two simultaneous focus traps and means the
  // existing dialog can take focus normally.
  const [openShareAfterConfirmation, setOpenShareAfterConfirmation] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { showError, showSuccess, showInfo } = useToast();
  const copy = copyForContext(context);

  useEffect(() => {
    if (isViewOnly || !autoOpen || creating || confirmationOpen) return;
    setConfirmationOpen(true);
    onAutoOpen?.();
  }, [autoOpen, confirmationOpen, creating, onAutoOpen, isViewOnly]);

  const closeConfirmation = () => {
    if (!creating) setConfirmationOpen(false);
  };

  const confirm = async () => {
    if (!mayNotify() || creating) return;
    setCreating(true);
    try {
      const result = onRequestNotification
        ? await onRequestNotification(browserRequestId())
        : await requestMarketResearchNotification(flowId, context, browserRequestId());
      if (result.no_counterpart === true) {
        // This remains recipient-private and informs both owner and non-owner
        // users through the app-wide bottom snackbar rather than stage UI.
        showInfo(copy.noRecipients);
        // Sharing remains owner-authorized by the existing FlowShareDialog
        // endpoint. Non-owners only see the safe snackbar above.
        if (isOwner && onOpenShare) setOpenShareAfterConfirmation(true);
      } else if (result.already_pending === true) {
        showInfo(context === 'questions'
          ? 'Mission partners have already been notified.'
          : 'A notification is already pending.');
      } else {
        // The server returns this transient action receipt only to the
        // authorized requester. The browser never derives recipients itself.
        showSuccess(createdNotificationCopy(context, result.notified_users));
      }
    } catch {
      showError('Unable to create the notification right now. Please try again.');
    } finally {
      setCreating(false);
      setConfirmationOpen(false);
    }
  };

  return (
    <>
      <Button
        ref={triggerRef}
        // The J&A handoff is the mission partner's terminal action, so
        // make it the clear primary action. Questions retains the quieter
        // outlined treatment above the generated-question list.
        variant={context === 'brand_name_ja' || context === 'product_discovery_review' || context === 'section_c_handoff' ? 'contained' : 'outlined'}
        disabled={isViewOnly || creating}
        onClick={() => {
          if (!mayNotify()) return;
          setConfirmationOpen(true);
        }}
      >
        {copy.button}
      </Button>
      <Dialog
        open={confirmationOpen}
        onClose={closeConfirmation}
        aria-labelledby="market-research-notify-title"
        TransitionProps={{
          onExited: () => {
            if (openShareAfterConfirmation) {
              setOpenShareAfterConfirmation(false);
              onOpenShare?.(context);
            } else {
              triggerRef.current?.focus();
            }
          },
        }}
      >
        <DialogTitle id="market-research-notify-title">
          {context === 'brand_name_ja' || context === 'section_c_handoff' ? 'Hand off to CO/CS?' : 'Create notification?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{copy.confirmation}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation} disabled={creating}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void confirm()}
            disabled={isViewOnly || creating}
            aria-label={creating
              ? (context === 'brand_name_ja' || context === 'section_c_handoff' ? 'Handing off to CO/CS' : 'Creating notification')
              : (context === 'brand_name_ja' || context === 'section_c_handoff' ? 'Confirm handoff to CO/CS' : 'Create notification')}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {creating
              ? (context === 'brand_name_ja' || context === 'section_c_handoff' ? 'Handing off to CO/CS' : 'Creating notification')
              : (context === 'brand_name_ja' || context === 'section_c_handoff' ? 'Handoff to CO/CS' : 'Create notification')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarketResearchNotifyControls;
